import { useContext, useEffect } from 'react';
import { useSocket } from '../../context/socket';
import { AuthContext } from '../../context/AuthContext';

export const GUEST_ATTRIBUTION_STORAGE_KEY = 'jg_guest_attrib';

export const getGuestSocketAuth = () => {
  if (typeof window === 'undefined') return { guest: 'true' };
  const params = new URLSearchParams(window.location.search);
  const pickParam = (key) => params.get(key) || null;
  const locale = window.navigator?.language || '';
  const localeRegion =
    (locale.includes('-') && locale.split('-')[1]) ||
    (locale.includes('_') && locale.split('_')[1]) ||
    null;

  const guestAuth = {
    guest: 'true',
    utm_source: pickParam('utm_source'),
    utm_medium: pickParam('utm_medium'),
    utm_campaign: pickParam('utm_campaign'),
    utm_content: pickParam('utm_content'),
    region: pickParam('region') || localeRegion,
    referrer: document.referrer || null,
  };

  Object.keys(guestAuth).forEach((key) => {
    if (guestAuth[key] === null || guestAuth[key] === '') delete guestAuth[key];
  });

  return guestAuth;
};

export default function useHitLabGuestPresence({
  enabled = true,
  onCount,
  onDiff,
  onSnapshot,
  source = 'hitlab_landing',
} = {}) {
  const { socket, getLatencyMs } = useSocket() || {};
  const { player } = useContext(AuthContext) || {};

  useEffect(() => {
    if (
      !enabled ||
      !socket ||
      typeof socket.on !== 'function' ||
      typeof socket.off !== 'function'
    ) {
      return undefined;
    }

    let heartbeatId = null;
    let recovering = false;
    const isAuthenticated = !!player?.token;
    const heartbeatSource = String(source || '').trim() || 'hitlab_landing';

    const configureGuestSocket = () => {
      if (isAuthenticated) return;
      const guestAuth = getGuestSocketAuth();
      const attribution = { ...guestAuth };
      delete attribution.guest;
      try {
        window.localStorage.setItem(
          GUEST_ATTRIBUTION_STORAGE_KEY,
          JSON.stringify(attribution),
        );
      } catch (err) {
        // Guest proof is supplemental; storage failures should not block the page.
      }

      if (socket.auth) {
        delete socket.auth.token;
      } else {
        socket.auth = {};
      }
      socket.auth = { ...(socket.auth || {}), ...guestAuth };
      if (!socket.connected && typeof socket.connect === 'function') {
        socket.connect();
      }
    };

    const emitHeartbeat = (activityType = 'active') => {
      if (typeof socket.emit !== 'function' || !socket.connected || !socket.id) {
        return;
      }
      const latencyMs = typeof getLatencyMs === 'function' ? getLatencyMs() : null;
      socket.emit('presence:heartbeat', {
        guest: true,
        sessionId: socket.id,
        activityType,
        source: heartbeatSource,
        latencyMs: Number.isFinite(Number(latencyMs))
          ? Math.round(Number(latencyMs))
          : null,
      });
    };

    const requestSnapshot = () => {
      if (typeof socket.emit !== 'function' || !socket.connected) return;
      socket.emit('guest:snapshot:request');
    };

    const startPresence = () => {
      const activityType =
        document.visibilityState === 'visible' ? 'active' : 'background';
      emitHeartbeat(activityType);
      requestSnapshot();
      if (!heartbeatId) {
        heartbeatId = setInterval(() => {
          const nextActivity =
            document.visibilityState === 'visible' ? 'active' : 'background';
          emitHeartbeat(nextActivity);
        }, 10000);
      }
    };

    const stopPresence = () => {
      if (heartbeatId) {
        clearInterval(heartbeatId);
        heartbeatId = null;
      }
    };

    const handleConnect = () => startPresence();
    const handleDisconnect = () => stopPresence();
    const handleClose = () => stopPresence();
    const handleVisibility = () => {
      const activityType =
        document.visibilityState === 'visible' ? 'active' : 'background';
      emitHeartbeat(activityType);
    };
    const handleFocus = () => emitHeartbeat('active');
    const handleBlur = () => emitHeartbeat('background');
    const handleGuestError = (err) => {
      if (!err?.message || !err.message.includes('unknown session')) return;
      stopPresence();
      if (recovering) return;
      recovering = true;
      if (typeof socket.disconnect === 'function') socket.disconnect();
      setTimeout(() => {
        if (typeof socket.connect === 'function') socket.connect();
        recovering = false;
      }, 200);
    };
    const handleSnapshot = (payload = {}) => {
      if (typeof onSnapshot === 'function') onSnapshot(payload);
    };
    const handleDiff = (payload = {}) => {
      if (typeof onDiff === 'function') onDiff(payload);
    };
    const handleCount = (payload = {}) => {
      if (typeof onCount === 'function') onCount(payload);
    };

    try {
      configureGuestSocket();
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      socket.on('close', handleClose);
      socket.on('guest:error', handleGuestError);
      socket.on('guest:snapshot', handleSnapshot);
      socket.on('guest:diff', handleDiff);
      socket.on('guest:count', handleCount);
      document.addEventListener('visibilitychange', handleVisibility);
      window.addEventListener('focus', handleFocus);
      window.addEventListener('blur', handleBlur);
      window.addEventListener('beforeunload', stopPresence);
      handleVisibility();
      if (socket.connected) startPresence();
    } catch (err) {
      return undefined;
    }

    return () => {
      try {
        stopPresence();
        document.removeEventListener('visibilitychange', handleVisibility);
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
        socket.off('close', handleClose);
        socket.off('guest:error', handleGuestError);
        socket.off('guest:snapshot', handleSnapshot);
        socket.off('guest:diff', handleDiff);
        socket.off('guest:count', handleCount);
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('blur', handleBlur);
        window.removeEventListener('beforeunload', stopPresence);
        if (!isAuthenticated && !socket.auth?.token) socket.auth = {};
      } catch (err) {
        // Presence proof is supplemental; landing rendering must stay quiet.
      }
    };
  }, [
    enabled,
    getLatencyMs,
    onCount,
    onDiff,
    onSnapshot,
    player?.token,
    socket,
    source,
  ]);
}
